import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ReactCompareSlider,
  ReactCompareSliderImage,
} from "react-compare-slider";
import {
  UploadSimple,
  Sparkle,
  DownloadSimple,
  ShareNetwork,
  ArrowsClockwise,
  Wrench,
} from "@phosphor-icons/react";

import UploadDropzone from "@/components/visualizer/UploadDropzone";
import FlakeGrid from "@/components/visualizer/FlakeGrid";
import ModeToggle from "@/components/visualizer/ModeToggle";
import QuoteDialog from "@/components/visualizer/QuoteDialog";
import { Button } from "@/components/ui/button";
import { fetchFlakes, aiRender, API } from "@/lib/api";
import { BrandMark } from "@/components/Brand";

// Full broadcast — we always coat the floor fully. No partial coverage option.
const FULL_BROADCAST_OPACITY = 0.85;

export default function Visualizer({ embed = false }) {
  const [flakes, setFlakes] = useState([]);
  const [selectedFlakeId, setSelectedFlakeId] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null); // data URI
  const [mode, setMode] = useState("quick"); // quick | ai
  const [aiResult, setAiResult] = useState(null); // data URI
  const [isRendering, setIsRendering] = useState(false);
  const [showCompare, setShowCompare] = useState(true);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const previewWrapRef = useRef(null);

  const selectedFlake = useMemo(
    () => flakes.find((f) => f.id === selectedFlakeId),
    [flakes, selectedFlakeId],
  );

  useEffect(() => {
    fetchFlakes()
      .then((d) => {
        const fullUrlFlakes = d.map((f) => ({
          ...f,
          image: f.image?.startsWith("/api/")
            ? `${API.replace(/\/api$/, "")}${f.image}`
            : f.image,
        }));
        setFlakes(fullUrlFlakes);
        if (fullUrlFlakes.length && !selectedFlakeId)
          setSelectedFlakeId(fullUrlFlakes[0].id);
      })
      .catch(() => toast.error("Could not load flake catalog."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset AI result whenever inputs change
  useEffect(() => {
    setAiResult(null);
  }, [uploadedImage, selectedFlakeId]);

  const handleUpload = (dataUri) => {
    setUploadedImage(dataUri);
    setAiResult(null);
  };

  const handleAiRender = async () => {
    if (!uploadedImage || !selectedFlake) {
      toast.error("Upload a photo and pick a flake first.");
      return;
    }
    setIsRendering(true);
    setAiResult(null);
    try {
      const { image_base64 } = await aiRender({
        imageBase64: uploadedImage,
        flakeId: selectedFlake.id,
      });
      setAiResult(image_base64);
      toast.success("AI render ready.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Render failed";
      toast.error(typeof msg === "string" ? msg : "Render failed");
    } finally {
      setIsRendering(false);
    }
  };

  // The image we present as "after" for the compare slider / downloads
  const afterImage =
    mode === "ai" && aiResult ? aiResult : uploadedImage; // for AI mode the rendered image, else original (overlay is layered via DOM)

  const downloadImage = async () => {
    if (!uploadedImage) return;
    let url;
    let filename = `epoxy-${selectedFlake?.id || "preview"}.png`;
    if (mode === "ai" && aiResult) {
      url = aiResult;
    } else {
      url = await composeQuickPreviewPng();
      if (!url) return;
    }
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const shareImage = async () => {
    try {
      const dataUri =
        mode === "ai" && aiResult ? aiResult : await composeQuickPreviewPng();
      if (!dataUri) return;
      const blob = await (await fetch(dataUri)).blob();
      const file = new File([blob], "epoxy-preview.png", { type: blob.type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My Epoxy Floor Preview",
        });
      } else {
        // fallback — copy URL not possible for data URIs; trigger download
        downloadImage();
        toast.message("Sharing not supported here — downloaded instead.");
      }
    } catch {
      /* ignored */
    }
  };

  // Build a PNG of the user's image + overlay for download/share in quick mode
  const composeQuickPreviewPng = async () => {
    if (!uploadedImage || !selectedFlake) return null;
    const baseImg = await loadImg(uploadedImage);
    const overlayImg = await loadImg(selectedFlake.image);
    const canvas = document.createElement("canvas");
    canvas.width = baseImg.width;
    canvas.height = baseImg.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(baseImg, 0, 0);
    ctx.globalAlpha = FULL_BROADCAST_OPACITY;
    ctx.globalCompositeOperation = selectedFlake.blend_mode || "overlay";
    // Tile or stretch — stretch is simplest and matches CSS preview
    ctx.drawImage(overlayImg, 0, 0, canvas.width, canvas.height);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    return canvas.toDataURL("image/png");
  };

  const loadImg = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  return (
    <div
      className="min-h-screen w-full text-white"
      style={{ background: "var(--epx-bg)" }}
      data-testid="visualizer-root"
      data-embed={embed ? "true" : "false"}
    >
      {!embed && <Header />}
      {embed && <EmbedHeader />}

      <main
        className={`w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${
          embed ? "pt-2 pb-8" : "pb-16"
        }`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Left / Main Preview Panel */}
          <section
            className="lg:col-span-8 flex flex-col gap-4 rounded-2xl border overflow-hidden min-h-[55vh] relative"
            style={{
              background: "var(--epx-surface)",
              borderColor: "var(--epx-border)",
            }}
            ref={previewWrapRef}
            data-testid="preview-panel"
          >
            <div className="flex items-center justify-between gap-3 px-5 pt-5">
              <div>
                <p className="overline">Preview</p>
                <h2 className="font-heading text-2xl font-semibold mt-1">
                  {uploadedImage ? "Your floor, reimagined" : "Upload a photo"}
                </h2>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <ModeToggle value={mode} onChange={setMode} />
              </div>
            </div>

            <div
              className="relative mx-4 mb-4 rounded-xl overflow-hidden epx-grain"
              style={{
                background: "#0b0c0e",
                border: "1px solid var(--epx-border)",
                aspectRatio: "16 / 10",
              }}
            >
              {!uploadedImage && (
                <div className="absolute inset-0 flex items-center justify-center p-6">
                  <UploadDropzone onFile={handleUpload} />
                </div>
              )}

              {uploadedImage && mode === "quick" && (
                <QuickPreviewLayer
                  baseImage={uploadedImage}
                  flake={selectedFlake}
                  showCompare={showCompare}
                />
              )}

              {uploadedImage && mode === "ai" && (
                <AiPreviewLayer
                  baseImage={uploadedImage}
                  aiResult={aiResult}
                  isRendering={isRendering}
                  showCompare={showCompare}
                />
              )}
            </div>

            {/* Mobile-only mode toggle */}
            {uploadedImage && (
              <div className="sm:hidden px-5 pb-3">
                <ModeToggle value={mode} onChange={setMode} />
              </div>
            )}

            {uploadedImage && (
              <div className="flex flex-wrap items-center gap-2 px-5 pb-5">
                <Button
                  variant="ghost"
                  className="h-9 px-3 text-zinc-300 hover:text-white hover:bg-white/5"
                  onClick={() => setShowCompare((s) => !s)}
                  data-testid="toggle-compare"
                >
                  <ArrowsClockwise size={16} className="mr-2" />
                  {showCompare ? "Hide before/after" : "Show before/after"}
                </Button>
                <Button
                  variant="ghost"
                  className="h-9 px-3 text-zinc-300 hover:text-white hover:bg-white/5"
                  onClick={() => {
                    setUploadedImage(null);
                    setAiResult(null);
                  }}
                  data-testid="reset-image"
                >
                  <UploadSimple size={16} className="mr-2" /> Upload new photo
                </Button>
                <div className="ml-auto flex gap-2">
                  <Button
                    variant="ghost"
                    className="h-9 px-3 text-zinc-300 hover:text-white hover:bg-white/5"
                    onClick={shareImage}
                    data-testid="share-button"
                  >
                    <ShareNetwork size={16} className="mr-2" /> Share
                  </Button>
                  <Button
                    variant="ghost"
                    className="h-9 px-3 text-zinc-300 hover:text-white hover:bg-white/5"
                    onClick={downloadImage}
                    data-testid="download-button"
                  >
                    <DownloadSimple size={16} className="mr-2" /> Download
                  </Button>
                </div>
              </div>
            )}
          </section>

          {/* Right / Sidebar Controls */}
          <aside className="lg:col-span-4 flex flex-col gap-6">
            {/* Flake selector */}
            <div
              className="rounded-2xl border p-5 sm:p-6"
              style={{
                background: "var(--epx-surface)",
                borderColor: "var(--epx-border)",
              }}
              data-testid="flake-section"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="overline">Step 02</p>
                  <h3 className="font-heading text-lg font-semibold mt-1">
                    Pick your flake blend
                  </h3>
                </div>
                <Sparkle
                  size={20}
                  weight="duotone"
                  style={{ color: "var(--epx-primary)" }}
                />
              </div>
              <FlakeGrid
                flakes={flakes}
                selectedId={selectedFlakeId}
                onSelect={setSelectedFlakeId}
              />
              {selectedFlake && (
                <p className="text-xs text-zinc-400 mt-4 leading-relaxed">
                  <span className="text-zinc-200 font-medium">
                    {selectedFlake.name}.
                  </span>{" "}
                  {selectedFlake.description}
                </p>
              )}
            </div>

            {/* AI render controls */}
            <div
              className="rounded-2xl border p-5 sm:p-6"
              style={{
                background: "var(--epx-surface)",
                borderColor: "var(--epx-border)",
              }}
              data-testid="controls-section"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="overline">Step 03</p>
                  <h3 className="font-heading text-lg font-semibold mt-1">
                    Make it photoreal
                  </h3>
                </div>
                <Sparkle
                  size={20}
                  weight="duotone"
                  style={{ color: "var(--epx-primary)" }}
                />
              </div>

              <div className="space-y-3" data-testid="ai-control">
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Every floor we install is a{" "}
                  <span className="text-zinc-200 font-medium">
                    full broadcast
                  </span>{" "}
                  — flakes are densely applied edge to edge.
                  {mode === "quick"
                    ? " Run the AI Realistic Render to see exactly how your finished floor will look."
                    : " Generate the photorealistic render below — takes 10–30s."}
                </p>
                <Button
                  onClick={() => {
                    if (mode !== "ai") setMode("ai");
                    handleAiRender();
                  }}
                  disabled={!uploadedImage || !selectedFlake || isRendering}
                  className="w-full h-12 font-semibold text-white"
                  style={{
                    background: "var(--epx-primary)",
                    boxShadow: "0 0 24px rgba(var(--epx-primary-rgb),0.30)",
                  }}
                  data-testid="ai-render-button"
                >
                  <Sparkle size={18} weight="fill" className="mr-2" />
                  {isRendering
                    ? "Rendering…"
                    : aiResult
                      ? "Re-render"
                      : "Generate AI render"}
                </Button>
              </div>
            </div>

            {/* Quote CTA */}
            <div
              className="rounded-2xl border p-5 sm:p-6 relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(160deg, rgba(var(--epx-primary-rgb),0.12) 0%, rgba(var(--epx-primary-rgb),0) 60%), var(--epx-surface)",
                borderColor: "var(--epx-border)",
              }}
              data-testid="cta-section"
            >
              <p className="overline">Love it?</p>
              <h3 className="font-heading text-xl font-semibold mt-1 mb-2">
                Get a free quote on this floor.
              </h3>
              <p className="text-sm text-zinc-400 mb-4">
                We&apos;ll review your photo, confirm pricing, and schedule a
                site visit.
              </p>
              <Button
                onClick={() => setQuoteOpen(true)}
                className="w-full h-12 font-semibold text-white"
                style={{
                  background: "var(--epx-primary)",
                  boxShadow: "0 0 24px rgba(var(--epx-primary-rgb),0.30)",
                }}
                data-testid="open-quote-button"
              >
                <Wrench size={18} weight="fill" className="mr-2" />
                Request my quote
              </Button>
            </div>
          </aside>
        </div>

        <FooterNote />
      </main>

      <QuoteDialog
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        flake={selectedFlake}
        previewBase64={
          mode === "ai" && aiResult ? aiResult : uploadedImage
        }
      />
    </div>
  );
}

function Header() {
  return (
    <header className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 pb-6">
      <div className="flex items-center justify-between gap-4">
        <BrandMark size="md" />
        <div className="hidden md:flex items-center gap-4">
          <a
            href="/embed-guide"
            className="text-xs text-zinc-300 hover:text-white font-mono-tech border border-white/10 hover:border-white/30 rounded-md px-3 py-1.5 transition-colors"
            data-testid="embed-guide-link"
          >
            &lt;/&gt; Embed on your site
          </a>
          <div className="flex items-center gap-2 text-xs text-zinc-400 font-mono-tech">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Powered by AI · Gemini Nano Banana
          </div>
        </div>
      </div>

      <div className="mt-8 sm:mt-10 max-w-3xl">
        <p className="overline">Step 01 → 02 → 03</p>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-2 leading-[1.05]">
          See your <span style={{ color: "var(--epx-primary)" }}>epoxy</span>{" "}
          floor before we pour it.
        </h2>
        <p className="text-zinc-400 text-base sm:text-lg mt-4 max-w-2xl leading-relaxed">
          Upload a photo of your garage, driveway, basement or room. Choose a
          flake blend. We&apos;ll show you exactly how it&apos;ll look — in
          seconds.
        </p>
      </div>
    </header>
  );
}

function FooterNote() {
  return (
    <p className="text-xs text-zinc-500 text-center mt-10 max-w-2xl mx-auto leading-relaxed">
      Renders are visualizations only. Final installed appearance may vary
      slightly based on lighting, base coat color and flake broadcast density.
    </p>
  );
}

function EmbedHeader() {
  return (
    <header
      className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 sm:pt-6 pb-4 flex items-center justify-between gap-4 border-b"
      style={{ borderColor: "var(--epx-border)" }}
    >
      <BrandMark size="sm" />
      <div className="hidden md:flex items-center gap-2 text-xs text-zinc-400 font-mono-tech">
        <span
          className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
          style={{ background: "var(--epx-primary)" }}
        />
        Powered by AI
      </div>
    </header>
  );
}

function QuickPreviewLayer({ baseImage, flake, showCompare }) {
  const opacity = FULL_BROADCAST_OPACITY;
  const blend = flake?.blend_mode || "overlay";

  const After = (
    <div className="relative w-full h-full">
      <img
        src={baseImage}
        alt="after"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {flake && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url("${flake.image}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: blend,
            opacity,
          }}
        />
      )}
      {/* subtle gloss to sell the wet-look */}
      {flake && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(120% 60% at 50% 110%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 50%)",
            mixBlendMode: "screen",
            opacity: 0.6,
          }}
        />
      )}
    </div>
  );

  if (!showCompare) {
    return <div className="absolute inset-0">{After}</div>;
  }

  return (
    <ReactCompareSlider
      className="absolute inset-0 [&>*]:!h-full"
      itemOne={
        <ReactCompareSliderImage
          src={baseImage}
          alt="before"
          style={{ objectFit: "cover" }}
        />
      }
      itemTwo={After}
      data-testid="compare-slider-quick"
    />
  );
}

function AiPreviewLayer({ baseImage, aiResult, isRendering, showCompare }) {
  if (isRendering) {
    return (
      <div className="absolute inset-0">
        <img
          src={baseImage}
          alt="rendering"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px] flex flex-col items-center justify-center gap-3">
          <div className="epx-scan-line" />
          <Sparkle
            size={32}
            weight="duotone"
            style={{ color: "var(--epx-primary)" }}
            className="animate-pulse"
          />
          <p className="font-heading text-lg font-semibold">
            Rendering your epoxy floor…
          </p>
          <p className="text-xs text-zinc-400 font-mono-tech">
            Photorealistic pass · 10–30s
          </p>
        </div>
      </div>
    );
  }

  if (!aiResult) {
    return (
      <div className="absolute inset-0">
        <img
          src={baseImage}
          alt="before"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/45">
          <Sparkle
            size={28}
            weight="duotone"
            style={{ color: "var(--epx-primary)" }}
          />
          <p className="font-heading text-base font-semibold">
            Ready when you are.
          </p>
          <p className="text-xs text-zinc-300 max-w-sm text-center">
            Click <span className="text-white">Generate AI render</span> to
            apply your selected flake blend with photorealistic detail.
          </p>
        </div>
      </div>
    );
  }

  if (!showCompare) {
    return (
      <img
        src={aiResult}
        alt="ai-result"
        className="absolute inset-0 w-full h-full object-cover"
      />
    );
  }

  return (
    <ReactCompareSlider
      className="absolute inset-0 [&>*]:!h-full"
      itemOne={
        <ReactCompareSliderImage
          src={baseImage}
          alt="before"
          style={{ objectFit: "cover" }}
        />
      }
      itemTwo={
        <ReactCompareSliderImage
          src={aiResult}
          alt="after"
          style={{ objectFit: "cover" }}
        />
      }
      data-testid="compare-slider-ai"
    />
  );
}
